import { ChangeEvent, useState } from 'react';
import { TransferRequestStatusEnum, type TransferRequestDocumentResponse } from '@shared/api/rest/transferRequests';
import { ColorsEnum, Text } from './text';
import { Icon } from './icon';
import CrossIcon from '@assets/icons/cross.svg';
import clsx from 'clsx';

interface PropsType {
  setSelectedItems: (files: File[]) => void;
  onFileUpload: (file: File) => Promise<void>;
  onFileDelete: (file: File) => Promise<void>;
  uploadedDocs?: TransferRequestDocumentResponse[];
  onDocDelete?: (id: string) => void;
  status?: string;
}

const acceptedFileExtensions = ['.jpeg', '.jpg', '.png', '.heic', '.heif', '.pdf', '.docx', '.doc', '.xlsx', '.xls', '.odt', '.ods'];

export const FileUpload = (props: PropsType) => {
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const isDisabled = props.status === TransferRequestStatusEnum.WaitingForApproval;

  const handleFileChange = (event: ChangeEvent<HTMLInputElement> & { files: FileList }) => {
    if (isDisabled) return;
    const newFilesArray = Array.from(event.target.files as FileList);
    processFiles(newFilesArray);
  };

  const handleDragOver = (event: React.DragEvent<HTMLLabelElement>) => event.preventDefault();

  const handleDrop = (event: React.DragEvent<HTMLLabelElement>) => {
    if (isDisabled) return;
    event.preventDefault();
    const files = Array.from(event.dataTransfer.files);
    processFiles(files);
  };

  const processFiles = async (filesArray: File[]) => {
    if (isDisabled) return;

    const newSelectedFiles: File[] = [...selectedFiles];
    let hasError = false;
    const fileTypeRegex = new RegExp(`(${acceptedFileExtensions.join('|').replace('.', '\\.')})$`, 'i');

    for (const file of filesArray) {
      if (newSelectedFiles.some((f) => f.name === file.name)) {
        hasError = true;
      } else if (!fileTypeRegex.test(file.name)) {
        hasError = true;
      } else {
        await props.onFileUpload(file);
        newSelectedFiles.push(file);
      }
    }

    if (!hasError) {
      setSelectedFiles(newSelectedFiles);
      props.setSelectedItems(newSelectedFiles);
    }
  };

  return (
    <>
      <div className='flex items-center justify-center w-full mt-4'>
        <label
          htmlFor='dropzone-file'
          className={clsx(
            'flex flex-col items-center justify-center w-full border-2 border-dashed rounded-lg',
            isDisabled ? 'border-gray-300 cursor-not-allowed' : 'border-dark-wet-asphalt cursor-pointer',
          )}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
        >
          <div className='flex items-center justify-center p-3'>
            <Text size='sm' color={ColorsEnum.Secondary}>
              <span className='mr-1'>Drag the file to the screen</span>
              or click to select it
            </Text>
          </div>
          <input
            id='dropzone-file'
            type='file'
            accept={acceptedFileExtensions.join(',')}
            className='hidden'
            onChange={handleFileChange}
            disabled={isDisabled}
          />
        </label>
      </div>
      {props.uploadedDocs && props.uploadedDocs.length > 0 && (
        <div className='flex flex-col mt-3'>
          {props.uploadedDocs.map((doc, idx) => (
            <div key={`${idx}_${doc.name}`} className='flex items-center justify-start gap-2 '>
              {doc?.name}
              <Icon
                icon={<CrossIcon width='16' height='16' />}
                className={clsx(isDisabled ? 'cursor-not-allowed' : 'cursor-pointer')}
                onClick={isDisabled ? undefined : () => props?.onDocDelete?.(doc.id)}
              />
            </div>
          ))}
        </div>
      )}
      {/* {selectedFiles.length > 0 && (
        <div className='flex flex-col mt-3'>
          {selectedFiles.map((file, idx) => (
            <div key={`${idx}_${file.name}`} onClick={() => deleteFile(file)} className='flex items-center justify-start gap-2'>
              {file?.name}
              <CrossIcon width='16' height='16' />
            </div>
          ))}
        </div>
      )} */}
    </>
  );
};
